/**
 * remove_from_cart — line removals and quantity decreases. (#160, GA4-E4-05)
 * add_to_cart — quantity INCREASES on the cart page, the half of #679 that never touches /cart/add.
 *
 * We can see what customers add but never what they take out, so products that are frequently added
 * then abandoned — the signal for price shock, delivery-cost shock or out-of-stock substitution —
 * are invisible.
 *
 * WHY THIS HOOKS THE NETWORK CALL AND NOT THE BUTTONS
 * Every cart mutation in this theme funnels through one place: `btyFetchCart()`
 * (`assets/theme.js:1191`) POSTs `{ id, quantity }` to `routes.cart_change_url`. Three different UI
 * paths reach it — the remove link (`.product-remove`, quantity 0), a typed quantity, and the
 * plus/minus buttons (which set `.quantity-input` and dispatch a synthetic `change`). Binding to
 * those three controls would mean re-deriving intent from each, and would silently miss any fourth
 * path. Wrapping the request observes all of them at the single point they agree on.
 *
 * It also avoids fighting the theme for the handlers. theme.js assigns `input.onchange = …` and
 * `remove.onclick = …` as properties, so a second `onchange` assignment would *replace* the theme's
 * and break the cart outright. This touches neither.
 *
 * HOW THE REMOVED QUANTITY IS KNOWN
 * `/cart/change.js` says "set line X to N"; it does not say what N was before. The previous quantity
 * comes from `data-gtm-qty`, stamped on each row by `analytics-cart-line-attrs.liquid`, snapshotted
 * BEFORE the request goes out. removed = previous - requested, and we only report when that is
 * positive. An increase is reported as `add_to_cart` (#679): the plus button and a typed higher
 * quantity add to the cart through /cart/change.js, so analytics-add-to-cart.js never sees them.
 *
 *   explicit remove   → requested 0        → removed = previous          (#160 step 1)
 *   3 → 1             → requested 1        → removed = 2                 (#160 step 2)
 *   decrement to zero → requested 0        → removed = full remaining    (#160 step 3)
 *
 * WHY IT CANNOT FIRE ON A POST-CHECKOUT CART CLEAR (#160 step 5)
 * A completed checkout empties the cart server-side, on Shopify's own domain, where no theme asset
 * runs. There is no `/cart/change.js` call to observe, so the event cannot fire — the requirement is
 * met structurally rather than by sniffing for a checkout we would have to guess at.
 *
 * THE CART DRAWER IS NOT COVERED BECAUSE THERE ISN'T ONE
 * #160 step 1 asks for the drawer too. This theme has no cart drawer — `sections/mini-cart.liquid`
 * and `snippets/side-cart-hover.liquid` are rendered by no template, and the live storefront emits
 * zero drawer markup. Established in #158; see docs/GA4-TRACKING-PLAN.md §5. If one is ever enabled,
 * this file needs no change *provided* its rows carry the same attributes and it mutates through
 * `cart_change_url` — the hook is on the request, not on the page.
 *
 * FAILURE POSTURE
 * Every step is guarded and the wrapper always returns the original promise. Analytics must never be
 * able to break the cart: a throw in here would otherwise take out add/remove/quantity site-wide.
 */
(function () {
  'use strict';

  if (!window.fetch) return;

  window.dataLayer = window.dataLayer || [];

  /**
   * Read the line straight from the DOM at request time, rather than from a snapshot kept in sync
   * with the cart.
   *
   * A cached snapshot is the obvious design and it is wrong here: `theme.js` replaces the cart
   * section's HTML after every change, so the cache has to be rebuilt on mutation, and any debounce
   * on that rebuild is a window in which a fast second click computes its delta from a stale
   * quantity — reporting the wrong number, silently, only under fast interaction. Reading on demand
   * has no such window and no state to invalidate.
   *
   * `data-gtm-qty` is server-rendered, but `theme.js` does NOT re-render the row after a quantity
   * change: `btyFetchCart()` only swaps the row's `.totals-item-price` (the row itself is replaced
   * only when the line is removed or the cart empties). Left alone, the attribute kept the page-load
   * quantity forever, so 2 → 3 → 2 reported nothing and 2 → 1 → 0 reported 2 removed for the second
   * step (#703). `syncLine()` below therefore keeps it current: optimistically when the request
   * goes out (so a fast second click reads the new value), then from Shopify's response, and back
   * to the old value if the request fails. Responses can land out of order, so each change takes a
   * per-line sequence number and only the newest one for that line may write back (the same guard
   * as the generation counter in `om-cart-stock-revalidate.liquid`).
   *
   * A cart can hold two lines for one variant (selling plans, line properties), and `data-id` is the
   * variant id. Like `theme.js` and `/cart/change.js` itself, this path is first-line-wins:
   * `findLine()` and `quantityIn()` read the first match, and `syncLine()` stamps every match.
   *
   * Scanning beats a `[data-id="…"]` selector: line ids can be line KEYS (`123:abc…`), and building
   * a selector from one means escaping user-adjacent data for no benefit at this size.
   */
  function findLine(id) {
    var rows = document.querySelectorAll('[data-gtm-item][data-gtm-qty][data-id]');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (String(row.getAttribute('data-id') || '') !== id) continue;
      var qty = parseInt(row.getAttribute('data-gtm-qty'), 10);
      if (isNaN(qty)) continue;
      var item;
      try { item = JSON.parse(row.getAttribute('data-gtm-item')); } catch (e) { continue; }
      if (!item || !item.item_id) continue;
      return { qty: qty, item: item };
    }
    return null;
  }

  /** Set `data-gtm-qty` on every rendered row for this line id. */
  function syncLine(id, qty) {
    var rows = document.querySelectorAll('[data-gtm-item][data-gtm-qty][data-id]');
    for (var i = 0; i < rows.length; i++) {
      if (String(rows[i].getAttribute('data-id') || '') === id) rows[i].setAttribute('data-gtm-qty', String(qty));
    }
  }

  /** The line's quantity in a /cart/change.js response, 0 if the line is gone, null if unreadable. */
  function quantityIn(cart, id) {
    if (!cart || !cart.items || typeof cart.items.length !== 'number') return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (String(it.id) === id || String(it.key) === id || String(it.variant_id) === id) return Number(it.quantity);
    }
    return 0;
  }

  function round2(n) { return Math.round(n * 100) / 100; }

  /**
   * Currency comes from `page_data` (#141), which every page pushes before this script runs.
   * `analytics-item.liquid` deliberately does not carry a per-item currency, and hardcoding 'INR'
   * here would be a second source of truth that silently goes wrong the day a second market opens.
   * The fallback is only reached if page_data is missing, which would itself be a bug worth seeing.
   */
  function currency() {
    var dl = window.dataLayer || [];
    for (var i = dl.length - 1; i >= 0; i--) {
      var pd = dl[i] && dl[i].page_data;
      if (pd && pd.currency) return pd.currency;
    }
    return 'INR';
  }

  function fire(entry, removedQty, eventName) {
    // Copy the item so the snapshot's own object is not mutated — the same line can be decremented
    // repeatedly, and each event must carry the quantity removed by THAT change.
    var item = {};
    for (var k in entry.item) { if (Object.prototype.hasOwnProperty.call(entry.item, k)) item[k] = entry.item[k]; }
    item.quantity = removedQty;

    var unit = typeof item.price === 'number' ? item.price : 0;

    // Clear the previous ecommerce object first. GA4 merges dataLayer state, so without this a later
    // event inherits this one's items[]. Same guard as analytics-impressions.js (#156).
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName || 'remove_from_cart',
      ecommerce: {
        currency: currency(),
        value: round2(unit * removedQty),
        items: [item]
      }
    });
  }

  function changeUrl() {
    var g = window.btyGlobals;
    return (g && g.cart_change_url) ? String(g.cart_change_url) : '/cart/change';
  }

  function isCartChange(url) {
    if (!url) return false;
    var u = String(url);
    // Match on the path fragment rather than an exact compare: the configured route may be
    // locale-prefixed (/en-in/cart/change.js) and callers may pass an absolute URL.
    return u.indexOf('/cart/change') !== -1 || u.indexOf(changeUrl()) !== -1;
  }

  function bodyOf(input, init) {
    if (init && typeof init.body === 'string') return init.body;
    if (input && typeof input === 'object' && typeof input.body === 'string') return input.body;
    return null;
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  var nativeFetch = window.fetch;
  var seq = {};   // line id -> number of the newest change sent for it

  window.fetch = function (input, init) {
    var tracked = null;
    try {
      if (isCartChange(urlOf(input))) {
        var raw = bodyOf(input, init);
        if (raw) {
          var payload = null;
          try { payload = JSON.parse(raw); } catch (e) { payload = null; }

          if (payload && payload.id !== undefined && payload.quantity !== undefined) {
            var id = String(payload.id);
            var requested = Number(payload.quantity);
            var entry = findLine(id);

            if (entry && !isNaN(requested)) {
              var removed = entry.qty - requested;
              // A decrease is a removal. An increase is an add (#679), reported only once the response
              // confirms it and with the quantity the cart actually took: a failed change must not
              // count, and Shopify can cap an increase at available stock.
              if (removed > 0) fire(entry, removed, 'remove_from_cart');
              syncLine(id, requested);
              seq[id] = (seq[id] || 0) + 1;
              tracked = { id: id, before: entry.qty, requested: requested, seq: seq[id], entry: entry };
            }
          }
        }
      }
    } catch (e) {
      // Never let instrumentation break a cart mutation.
    }

    // Applied to `window`, never `this`: a detached call arrives with `this` undefined (see
    // analytics-attribution.js).
    var result = nativeFetch.apply(window, arguments);
    if (!tracked) return result;

    // Reconcile from the server, on a clone, without touching what the caller receives.
    // A newer change for the same line has superseded this one, so this response must not write.
    var line = tracked;
    var stale = function () { return seq[line.id] !== line.seq; };
    try {
      result.then(function (response) {
        try {
          if (!response || !response.ok) { if (!stale()) syncLine(line.id, line.before); return; }
          response.clone().json().then(function (cart) {
            var q = quantityIn(cart, line.id);
            if (q === null || isNaN(q)) return;
            // The add is reported even when a newer click has superseded this response, or a fast
            // double click would lose its first add. Capped at what THIS change asked for: if the
            // server applied a later change before answering, q already includes it.
            var added = Math.min(q, line.requested) - line.before;
            if (added > 0) fire(line.entry, added, 'add_to_cart');
            if (!stale()) syncLine(line.id, q);
          }, function () { /* not JSON: nothing to report */ });
        } catch (e) { /* measurement only */ }
      }, function () { try { if (!stale()) syncLine(line.id, line.before); } catch (e) {} });
    } catch (e) { /* measurement only */ }
    return result;
  };

  // No init step and no readiness check on purpose. Reading the line on demand means there is
  // nothing to prime at startup and nothing to keep in sync afterwards, so the wrapper is correct
  // from the moment this file executes — including for a cart mutation that somehow happens before
  // DOMContentLoaded, which a snapshot built at load time would have missed entirely.
})();
